import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "@env/environment";
import { OfflineStorageService } from "./offline-storage.service";
import { ExecutionTemplateSyncService } from "./execution-template-sync.service";
import { NetworkService } from "../core/services/network.service";
import { SyncQueueService } from "./sync-queue.service";
import { TenantDateService } from "../shared/services/tenant-date.service";
import { InventoryLocalService } from "../features/inventory/inventory-local.service";
import type {
  LocalWorkOrder,
  LocalAssetDetails,
  LocalVerticalConfig,
} from "./offline-storage.types";
import {
  getServiceMode,
  type Vertical,
  type MyDailyLoad,
  type Location,
} from "@fsm/shared";

interface SuccessResponse<T> {
  data: T;
  requestId: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: { nextCursor: string | null; hasMore: boolean };
  requestId: string;
}

interface TenantConfigData {
  vertical: string;
  enabledModules: string[];
  /**
   * Add-ons activos del tenant. `inspection_points` NO mapea a un PlanModule
   * (su gating es por add-on, no vía enabledModules), así que llega aquí y el
   * móvil lo refleja en el CSV de módulos cacheado para gatear la sección de
   * puntos igual que inventory/gps. @see plan-limits.ts ADD_ON_ENABLED_MODULE
   */
  addOns?: string[];
  timezone?: string;
  country?: string;
  /**
   * Tenant business hours (end-of-day per weekday). Drives the GPS auto-stop
   * at the end of the workday (spec gps-route-optimization Req 13.4). Only
   * present for in-house tenants; optional/absent otherwise.
   */
  businessHours?: {
    timezone: string;
    schedule: { day: number; open: string; close: string }[];
  };
  verticalConfig: {
    checklistTemplates: unknown[];
    fieldDataSections: unknown[];
    assetFields?: unknown[];
    assetTypes?: unknown[];
    assetGenericLabels?: unknown;
    assetRegistrationMode?: string;
    assetRegistrationHint?: string;
    requirePhotos?: boolean;
    requireSignature?: boolean;
    inspectionPointsPhotoSource?: string;
  };
}

interface WorkOrderDetailData {
  details: Record<string, unknown>;
}

/**
 * InitialDownloadService — Handles the data download sequence on app open.
 *
 * Sequence:
 * 1. GET /v1/tenant/config → save verticalConfig
 * 2. GET /v1/work-orders/mine?status=ASSIGNED,IN_PROGRESS → save to SQLite
 * 3. For each WO with assetId: GET /v1/assets/:id → save asset_details
 * 4. For each IN_PROGRESS WO: GET /v1/work-orders/:id → save WorkOrderDetails
 *
 * Features:
 * - Displays last sync timestamp
 * - Handles partial failure (shows what loaded/failed, offers retry)
 * - Background refresh on foreground without blocking UI
 * - Detects verticalConfig changes since last sync
 */
@Injectable({ providedIn: "root" })
export class InitialDownloadService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(OfflineStorageService);
  private readonly network = inject(NetworkService);
  private readonly templateSync = inject(ExecutionTemplateSyncService);
  private readonly syncQueue = inject(SyncQueueService);
  private readonly tenantDate = inject(TenantDateService);
  private readonly inventoryLocal = inject(InventoryLocalService);

  /** Whether a download is currently in progress */
  readonly downloading = signal(false);

  /** Last successful sync timestamp */
  readonly lastSyncAt = signal<string | null>(null);

  /** Download progress description */
  readonly progressMessage = signal<string | null>(null);

  /** Errors encountered during download */
  readonly errors = signal<string[]>([]);

  /** Whether verticalConfig changed since last sync */
  readonly configChanged = signal(false);

  private readonly apiUrl = environment.apiUrl;

  /** In-flight download promise — allows concurrent callers to await the same download */
  private inFlightDownload: Promise<LocalWorkOrder[]> | null = null;

  /** Last successful download result — used to skip redundant downloads within cooldown */
  private lastDownloadResult: LocalWorkOrder[] | null = null;
  private lastDownloadTimestamp = 0;

  /** Cooldown (ms) — skip downloadAll if last successful download was within this window */
  private static readonly DOWNLOAD_COOLDOWN_MS = 8_000;

  /**
   * Execute the full download sequence.
   * Can be called on app open and on pull-to-refresh.
   * Returns the downloaded orders (for direct use when SQLite is unavailable on web).
   * If a download is already in progress, returns the same promise (avoids duplicate calls).
   * If a download completed recently (within cooldown), returns cached result.
   */
  async downloadAll(force = false): Promise<LocalWorkOrder[]> {
    // Return cached result if within cooldown (prevents redundant network calls)
    if (
      !force &&
      this.lastDownloadResult !== null &&
      Date.now() - this.lastDownloadTimestamp <
        InitialDownloadService.DOWNLOAD_COOLDOWN_MS
    ) {
      return this.lastDownloadResult;
    }

    if (this.inFlightDownload) {
      try {
        return await this.inFlightDownload;
      } catch {
        // Previous download failed — allow retry
        this.inFlightDownload = null;
      }
    }

    this.inFlightDownload = this.executeDownload();
    try {
      const result = await this.inFlightDownload;
      this.lastDownloadResult = result;
      this.lastDownloadTimestamp = Date.now();
      return result;
    } finally {
      this.inFlightDownload = null;
    }
  }

  /** Max concurrent requests for batch downloads (avoid overwhelming the server/device) */
  private static readonly BATCH_CONCURRENCY = 5;

  private async executeDownload(): Promise<LocalWorkOrder[]> {
    this.downloading.set(true);
    this.errors.set([]);
    this.configChanged.set(false);

    const downloadErrors: string[] = [];
    let downloadedOrders: LocalWorkOrder[] = [];

    try {
      // ─── Phase 1: Config + Templates + Work Orders in PARALLEL ─────────────
      // These three are independent — run simultaneously to cut initial wait time.
      this.progressMessage.set("Descargando datos...");

      const [configSuccess, templateSyncSuccess, orders] = await Promise.all([
        this.downloadTenantConfig(),
        this.templateSync.sync(),
        this.downloadWorkOrders(),
      ]);

      if (!configSuccess) {
        downloadErrors.push("Configuración del tenant");
      }
      if (!templateSyncSuccess) {
        downloadErrors.push("Plantillas de ejecución");
      }
      if (orders === null) {
        downloadErrors.push("Órdenes de trabajo");
        throw new Error("DOWNLOAD_FAILED_WORK_ORDERS");
      } else {
        downloadedOrders = orders;
      }

      // ─── Phase 2: Customer data + WO details in PARALLEL batches ───────────
      // Note: individual asset downloads are skipped because downloadCustomerAssets
      // already fetches ALL assets for each customer (superset of WO-linked assets).
      if (orders.length > 0) {
        this.progressMessage.set("Descargando detalles...");

        // Collect unique customer IDs (with fallback name for cache)
        const customerMap = new Map<string, string>();
        for (const order of orders) {
          if (!customerMap.has(order.customerId)) {
            customerMap.set(order.customerId, order.customerName);
          }
        }

        // Collect IN_PROGRESS work order IDs for detail download
        const inProgressWoIds = orders
          .filter((o) => o.status === "IN_PROGRESS")
          .map((o) => o.workOrderId);

        // Run customer downloads and WO detail downloads in parallel batches
        const [, woDetailResults] = await Promise.all([
          // Batch download customers + their assets (concurrency-limited)
          // This covers individual asset details too (customer assets is a superset)
          this.batchDownload(
            [...customerMap.entries()],
            async ([customerId, customerName]) => {
              await this.downloadAndCacheCustomer(customerId, customerName);
              await this.downloadCustomerAssets(customerId);
              return true;
            },
          ),
          // Batch download WO details (concurrency-limited)
          this.batchDownload(inProgressWoIds, (id) =>
            this.downloadWorkOrderDetails(id),
          ),
        ]);

        // Collect WO detail errors
        inProgressWoIds.forEach((id, i) => {
          if (!woDetailResults[i]) {
            downloadErrors.push(`Detalles OT ${id.substring(0, 8)}...`);
          }
        });
      }

      // ─── Phase 3: Daily load (depends on config being saved from Phase 1) ──
      const configForModules = await this.storage.getVerticalConfig();
      const enabledModules = configForModules?.enabledModules?.split(",") ?? [];
      if (enabledModules.includes("inventory_basic")) {
        this.progressMessage.set("Descargando materiales del día...");
        const loadSuccess = await this.downloadDailyLoad();
        if (!loadSuccess) {
          downloadErrors.push("Materiales del día");
        }
      }

      // Update last sync timestamp
      if (downloadErrors.length === 0) {
        this.lastSyncAt.set(new Date().toISOString());
      }
    } finally {
      this.downloading.set(false);
      this.progressMessage.set(null);
      this.errors.set(downloadErrors);
    }

    return downloadedOrders;
  }

  /**
   * Execute async tasks in batches with concurrency control.
   * Prevents overwhelming the server with too many simultaneous requests
   * while still being significantly faster than sequential execution.
   */
  private async batchDownload<T, R>(
    items: T[],
    fn: (item: T) => Promise<R>,
  ): Promise<R[]> {
    const results: R[] = [];
    const concurrency = InitialDownloadService.BATCH_CONCURRENCY;

    for (let i = 0; i < items.length; i += concurrency) {
      const batch = items.slice(i, i + concurrency);
      const batchResults = await Promise.allSettled(batch.map(fn));
      for (const r of batchResults) {
        results.push(
          r.status === "fulfilled" ? r.value : (false as unknown as R),
        );
      }
    }

    return results;
  }

  /**
   * Lightweight poll — fetches only work orders from the API without
   * re-downloading config, templates, assets, or customer data.
   * Used by the periodic polling to reduce bandwidth and latency.
   * Returns the active orders or null if the fetch fails.
   */
  async fetchOrdersOnly(): Promise<LocalWorkOrder[] | null> {
    return this.downloadWorkOrders();
  }

  /** Load last sync timestamp from storage */
  async loadLastSyncTimestamp(): Promise<void> {
    const ts = await this.storage.getLastSyncTimestamp();
    this.lastSyncAt.set(ts);
  }

  // ─── Private download methods ──────────────────────────────────────────────

  private async downloadTenantConfig(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<SuccessResponse<TenantConfigData>>(
          `${this.apiUrl}/v1/tenant/config`,
        ),
      );

      // Legacy tenants may not have a vertical selected nor a verticalConfig
      // persisted yet. Capabilities (notably gps_tracking from the Business
      // plan) must still be cached; use an empty UI config instead of aborting
      // the whole tenant-config download.
      const config: TenantConfigData["verticalConfig"] = res.data
        .verticalConfig ?? {
        checklistTemplates: [],
        fieldDataSections: [],
      };
      const now = new Date().toISOString();

      // Check if config changed since last sync
      const existing = await this.storage.getVerticalConfig();
      if (existing) {
        const oldTemplates = existing.checklistTemplatesJson;
        const newTemplates = JSON.stringify(config.checklistTemplates);
        const oldSections = existing.fieldDataSectionsJson;
        const newSections = JSON.stringify(config.fieldDataSections);

        if (oldTemplates !== newTemplates || oldSections !== newSections) {
          this.configChanged.set(true);
        }
      }

      // Derive serviceMode from tenant vertical
      const serviceMode = getServiceMode(res.data.vertical as Vertical);

      const localConfig: LocalVerticalConfig = {
        id: "current",
        checklistTemplatesJson: JSON.stringify(config.checklistTemplates),
        fieldDataSectionsJson: JSON.stringify(config.fieldDataSections),
        assetFieldsJson: config.assetFields
          ? JSON.stringify(config.assetFields)
          : undefined,
        assetTypesJson: config.assetTypes
          ? JSON.stringify(config.assetTypes)
          : undefined,
        assetGenericLabelsJson: config.assetGenericLabels
          ? JSON.stringify(config.assetGenericLabels)
          : undefined,
        assetRegistrationMode: config.assetRegistrationMode,
        assetRegistrationHint: config.assetRegistrationHint,
        requirePhotos: config.requirePhotos ?? false,
        requireSignature: config.requireSignature ?? false,
        // Autocreación de OT por el técnico (spec technician-self-created-work-order).
        // Vive dentro de verticalConfig; SQLite lo guarda como 0/1 (fail-closed).
        technicianSelfCreateOrders: (
          config as unknown as Record<string, unknown>
        )["technicianSelfCreateOrders"]
          ? 1
          : 0,
        // Opciones de tipo de servicio del tenant para el selector de la
        // autocreación de OT (spec technician-self-created-work-order).
        serviceTypeOptionsJson: (config as unknown as Record<string, unknown>)[
          "serviceTypeOptions"
        ]
          ? JSON.stringify(
              (config as unknown as Record<string, unknown>)[
                "serviceTypeOptions"
              ],
            )
          : undefined,
        serviceMode,
        inspectionPointsPhotoSource: config.inspectionPointsPhotoSource,
        // inspection_points es un add-on que NO mapea a PlanModule, así que no
        // llega en enabledModules; lo añadimos al CSV local si el add-on está
        // activo para gatear la sección de puntos igual que los demás módulos.
        enabledModules: [
          ...(res.data.enabledModules ?? []),
          ...(res.data.addOns?.includes("inspection_points")
            ? ["inspection_points"]
            : []),
          // home_care_suite tampoco mapea a PlanModule; se refleja igual para
          // gatear las llamadas de la vertical (p.ej. intake del receptor).
          ...(res.data.addOns?.includes("home_care_suite")
            ? ["home_care_suite"]
            : []),
        ].join(","),
        timezone: res.data.timezone,
        country: res.data.country,
        businessHoursJson: res.data.businessHours
          ? JSON.stringify(res.data.businessHours)
          : undefined,
        inspectionPointGpsRadiusMeters: (
          res.data as unknown as Record<string, unknown>
        )["inspectionPointGpsRadiusMeters"] as number | undefined,
        // On/off de la verificación GPS de puntos (spec inspection-point-gps-settings).
        // Top-level Tenant → se lee de res.data. SQLite guarda 0/1 (undefined si no viene).
        inspectionPointGpsEnabled: (() => {
          const v = (res.data as unknown as Record<string, unknown>)[
            "inspectionPointGpsEnabled"
          ];
          return v === undefined ? undefined : v ? 1 : 0;
        })(),
        vertical: res.data.vertical,
        technicianExpensesEnabled: (
          res.data as unknown as Record<string, unknown>
        )["settlementConfig"]
          ? ((
              (res.data as unknown as Record<string, unknown>)[
                "settlementConfig"
              ] as { technicianExpensesEnabled?: boolean }
            )?.technicianExpensesEnabled ?? false)
          : false,
        technicianIncomeEnabled: (
          res.data as unknown as Record<string, unknown>
        )["settlementConfig"]
          ? ((
              (res.data as unknown as Record<string, unknown>)[
                "settlementConfig"
              ] as { technicianIncomeEnabled?: boolean }
            )?.technicianIncomeEnabled ?? false)
          : false,
        updatedAt: now,
      };

      await this.storage.saveVerticalConfig(localConfig);
      // Update tenant date service with the downloaded timezone
      this.tenantDate.setTimezone(res.data.timezone);
      return true;
    } catch (err) {
      console.error("[InitialDownload] tenant config download failed", err);
      return false;
    }
  }

  private async downloadWorkOrders(): Promise<LocalWorkOrder[] | null> {
    try {
      // Paginate through all results (API may split across pages)
      let allOrders: LocalWorkOrder[] = [];
      let cursor: string | null = null;
      let hasMore = true;

      while (hasMore) {
        const reqUrl: string = cursor
          ? `${this.apiUrl}/v1/work-orders/mine?status=ASSIGNED,IN_PROGRESS&limit=100&cursor=${encodeURIComponent(cursor)}`
          : `${this.apiUrl}/v1/work-orders/mine?status=ASSIGNED,IN_PROGRESS&limit=100`;

        const res: PaginatedResponse<LocalWorkOrder> = await firstValueFrom(
          this.http.get<PaginatedResponse<LocalWorkOrder>>(reqUrl),
        );

        allOrders = [...allOrders, ...res.data];
        cursor = res.pagination?.nextCursor ?? null;
        hasMore = res.pagination?.hasMore ?? false;
      }

      const orders = allOrders;

      // ─── Single SQLite read for existing orders (used for isNew detection + status protection)
      // Consolidates what was previously 3 separate getWorkOrders() calls.
      let existingOrders: LocalWorkOrder[] = [];
      try {
        existingOrders = await this.storage.getWorkOrders();
      } catch {
        // SQLite unavailable (web) — proceed without local state
      }
      const existingIds = existingOrders.map((o) => o.workOrderId);
      const locallyCompletedIds = new Set(
        existingOrders
          .filter((o) => o.status === "COMPLETED")
          .map((o) => o.workOrderId),
      );

      const enrichedOrders = orders.map((o) => ({
        ...o,
        isNew: existingIds.includes(o.workOrderId) ? 0 : 1,
      }));

      // Get work order IDs that have a pending COMPLETE in the sync queue.
      // These should NOT have their status overwritten by the server response
      // (server still shows IN_PROGRESS until the COMPLETE is synced).
      let pendingCompleteIds = new Set<string>();
      // Get work order IDs that have ANY pending operation in the sync queue.
      // Used to prevent overwriting local IN_PROGRESS with server's stale ASSIGNED.
      let pendingAnyIds = new Set<string>();
      try {
        pendingCompleteIds =
          await this.syncQueue.getWorkOrderIdsWithPendingComplete();
        // Also include recently completed WOs (COMPLETE succeeded but GSI not propagated yet)
        const recentlyCompleted =
          this.syncQueue.getRecentlyCompletedWorkOrderIds();
        for (const id of recentlyCompleted) pendingCompleteIds.add(id);
      } catch {
        // SQLite unavailable — no protection needed
      }
      try {
        pendingAnyIds = await this.syncQueue.getPendingWorkOrderIds();
      } catch {
        // SQLite unavailable — fall back to only protecting COMPLETE operations
        pendingAnyIds = pendingCompleteIds;
      }

      // ─── DIAGNOSTIC: Log protection state for debugging sync issues ─────────
      console.info("[InitialDownload] downloadWorkOrders — protection state:", {
        serverOrderCount: orders.length,
        pendingCompleteCount: pendingCompleteIds.size,
        pendingAnyCount: pendingAnyIds.size,
      });

      // Purge stale sync queue operations for work orders that are no longer active.
      // The server's /mine response is authoritative: if a WO is not in ASSIGNED/IN_PROGRESS,
      // it was completed or cancelled externally, and any pending local ops are stale.
      const activeWoIds = new Set(orders.map((o) => o.workOrderId));
      // Also keep WOs with pending COMPLETE (they're locally completed, server just hasn't confirmed yet)
      for (const id of pendingCompleteIds) activeWoIds.add(id);
      try {
        await this.syncQueue.purgeStaleOperations(activeWoIds);
      } catch {
        // Non-critical — stale ops will be cleaned up on next 409 cascade
      }

      // Persist to SQLite (best-effort — may fail on web)
      try {
        // ─── PROTECTION: Never downgrade a locally COMPLETED order ─────────────
        // The server's GSI (DynamoDB eventually-consistent index) may still return
        // the WO as IN_PROGRESS for several minutes after the COMPLETE was synced.
        // Uses locallyCompletedIds from the single read above.

        // Merge all protection sources: pending COMPLETE ops + recently completed + locally COMPLETED
        const protectedCompleteIds = new Set([
          ...pendingCompleteIds,
          ...locallyCompletedIds,
        ]);

        console.info("[InitialDownload] SQLite protection:", {
          locallyCompletedCount: locallyCompletedIds.size,
          protectedCompleteCount: protectedCompleteIds.size,
        });

        // Save ALL orders to SQLite. For orders with pending sync operations,
        // preserve their local status (don't overwrite with stale server status).
        const ordersToSave = enrichedOrders.map((o) => {
          if (protectedCompleteIds.has(o.workOrderId)) {
            return { ...o, status: "COMPLETED" as LocalWorkOrder["status"] };
          }
          if (pendingAnyIds.has(o.workOrderId) && o.status === "ASSIGNED") {
            // Server still shows ASSIGNED but local has pending STATUS_TRANSITION
            return {
              ...o,
              status: "IN_PROGRESS" as LocalWorkOrder["status"],
            };
          }
          return o;
        });
        await this.storage.saveWorkOrders(ordersToSave);

        // Remove stale orders from SQLite (cancelled/completed externally)
        // Keep orders with pending operations — they have local changes not yet synced
        const activeIdsToKeep = [
          ...enrichedOrders.map((o) => o.workOrderId),
          ...pendingAnyIds,
        ];
        await this.storage.removeStaleWorkOrders(activeIdsToKeep);
      } catch {
        // SQLite unavailable — orders are still returned for in-memory use
      }

      // For in-memory use: preserve local status for orders with pending sync operations.
      // - protectedComplete (pending COMPLETE + recently completed + locally COMPLETED) → force COMPLETED
      // - pendingAny (non-complete) → keep server status but mark as IN_PROGRESS
      //   (if the local status was IN_PROGRESS due to a pending STATUS_TRANSITION)
      //
      // Uses locallyCompletedIds from the single SQLite read above.
      const allProtectedCompleteIds = new Set([
        ...pendingCompleteIds,
        ...locallyCompletedIds,
      ]);

      const finalOrders = enrichedOrders.map((o) => {
        if (allProtectedCompleteIds.has(o.workOrderId)) {
          return { ...o, status: "COMPLETED" as LocalWorkOrder["status"] };
        }
        if (pendingAnyIds.has(o.workOrderId) && o.status === "ASSIGNED") {
          // Local has pending STATUS_TRANSITION → keep as IN_PROGRESS
          return { ...o, status: "IN_PROGRESS" as LocalWorkOrder["status"] };
        }
        return o;
      });

      return finalOrders;
    } catch {
      return null;
    }
  }

  /**
   * Download customer details and cache for template resolution (Req 5.5).
   * Fetches executionTemplateId from GET /v1/customers/:id if available,
   * otherwise caches with null templateId.
   */
  private async downloadAndCacheCustomer(
    customerId: string,
    fallbackName: string,
  ): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<
          SuccessResponse<{
            customerId: string;
            name: string;
            executionTemplateId?: string | null;
            locations?: Location[];
          }>
        >(`${this.apiUrl}/v1/customers/${customerId}`),
      );
      await this.storage.saveCustomer({
        customerId: res.data.customerId,
        name: res.data.name,
        executionTemplateId: res.data.executionTemplateId ?? null,
        // Cachea las sedes para la autocreación de OT offline (spec
        // technician-self-create-offline Req 2). GET /v1/customers/:id ya las trae.
        locationsJson: JSON.stringify(res.data.locations ?? []),
      });
    } catch {
      // API failed — cache with data from work order (no executionTemplateId)
      try {
        await this.storage.saveCustomer({
          customerId,
          name: fallbackName,
          executionTemplateId: null,
        });
      } catch {
        // SQLite unavailable — best effort
      }
    }
  }

  private async downloadAssetDetails(assetId: string): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<SuccessResponse<LocalAssetDetails>>(
          `${this.apiUrl}/v1/assets/${assetId}`,
        ),
      );

      await this.storage.saveAssetDetails(res.data);
      return true;
    } catch {
      return false;
    }
  }

  /** Download all assets for a customer and cache them in SQLite for offline access */
  private async downloadCustomerAssets(customerId: string): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ data: LocalAssetDetails[]; requestId: string }>(
          `${this.apiUrl}/v1/customers/${customerId}/assets?limit=50`,
        ),
      );
      const assets = res.data ?? [];
      if (assets.length === 0) return;

      // Batch save all assets in a single transaction (1 disk flush instead of N)
      try {
        await this.storage.saveAssetDetailsBatch(assets);
      } catch {
        // Transaction failed — fall back to individual saves
        for (const asset of assets) {
          try {
            await this.storage.saveAssetDetails(asset);
          } catch {
            // SQLite error for individual asset — continue with others
          }
        }
      }
    } catch {
      // API failed — non-critical, assets will be available if individually cached
    }
  }

  private async downloadWorkOrderDetails(
    workOrderId: string,
  ): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<SuccessResponse<WorkOrderDetailData>>(
          `${this.apiUrl}/v1/work-orders/${workOrderId}`,
        ),
      );

      await this.storage.saveWorkOrderDetails(workOrderId, res.data.details);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Download the technician's daily load (Maleta Virtual) from the API.
   * Stores it in SQLite for offline consumption during OT execution.
   *
   * @see steering 30-inventario-maleta-virtual.md §8.3
   */
  private async downloadDailyLoad(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<SuccessResponse<{ load: MyDailyLoad | null }>>(
          `${this.apiUrl}/v1/inventory/my-load`,
        ),
      );

      const load = res.data.load;
      if (load) {
        await this.inventoryLocal.saveDailyLoad({
          loadId: load.loadId,
          date: load.date,
          status: load.status,
          items: load.items,
          createdAt: new Date().toISOString(),
        });
      }
      // Clean up expired loads from previous days
      await this.inventoryLocal.clearExpiredLoads();
      return true;
    } catch {
      return false;
    }
  }
}
